import { useCallback, useState, useEffect } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Button,
  NumberInput,
  Divider,
  Badge,
  ActionIcon,
  Loader,
  Alert,
  Tooltip,
} from "@mantine/core";
import {
  IconPlus,
  IconPrinter,
  IconPackage,
  IconTrash,
  IconAlertCircle,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  useSpecimenBlocks,
  useAddGrossingBlocks,
  useDeleteGrossingBlock,
  usePrintBlockLabels,
} from "../../hooks/useWorkflows";
import { GrossingBlock } from "../../services/workflows.service";
import type { CassetteLabel } from "../accessioning/CassetteLabelPreview";
import { useCassettePrint } from "../../hooks/useCassettePrint";

import {
  printSelectedCassette,
  selectedPrintError,
} from "../../services/selectedMaterialPrint.service";

// Re-export for backward compatibility
export interface Block {
  id: string;
  number: number;
  isNew?: boolean;
  isAdditional?: boolean;
}

interface BlockManagementCardProps {
  caseId: string;
  specimenId: string;
  specimenLetter: string;
  disabled?: boolean;
}

export default function BlockManagementCard({
  caseId,
  specimenId,
  specimenLetter,
  disabled = false,
}: BlockManagementCardProps) {
  const [addCount, setAddCount] = useState<number | string>(1);
  const [selectedBlockIds, setSelectedBlockIds] = useState<string[]>([]);

  // Fetch blocks from API
  const {
    data: blocks = [],
    isLoading,
    error,
    refetch,
  } = useSpecimenBlocks(specimenId);

  // Mutations
  const addBlocksMutation = useAddGrossingBlocks();
  const deleteBlockMutation = useDeleteGrossingBlock();
  const printLabelsMutation = usePrintBlockLabels();
  const { printLabels } = useCassettePrint();

  const [printingBlockId, setPrintingBlockId] = useState<string>();
  const handlePrintBlock = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      const blockId = event.currentTarget.dataset.blockId;
      if (!blockId || disabled || printingBlockId) return;
      setPrintingBlockId(blockId);
      try {
        const result = await printLabelsMutation.mutateAsync({
          caseId,
          blockIds: [blockId],
        });
        if (result.labels.length !== 1)
          throw new Error("Expected one cassette label");
        await printSelectedCassette(result.labels[0]);
        notifications.show({
          title: "Block Printed",
          message: "The selected cassette label was printed.",
          color: "green",
        });
      } catch (error) {
        notifications.show({
          title: "Print Failed",
          message: selectedPrintError(error),
          color: "red",
        });
      } finally {
        setPrintingBlockId(undefined);
      }
    },
    [caseId, disabled, printingBlockId, printLabelsMutation]
  );

  // Reset selection when specimen changes
  useEffect(() => {
    setSelectedBlockIds([]);
  }, [specimenId]);

  const handleAddBlocks = async () => {
    const count =
      typeof addCount === "number" ? addCount : parseInt(String(addCount), 10);

    if (!count || count < 1 || count > 20) {
      notifications.show({
        title: "Invalid Count",
        message: "Please enter a number between 1 and 20",
        color: "red",
      });
      return;
    }

    try {
      const newBlocks = await addBlocksMutation.mutateAsync({
        caseId,
        specimenId,
        count,
      });

      setAddCount(1);

      notifications.show({
        title: "Blocks Added",
        message: `Added ${newBlocks.length} block${
          newBlocks.length > 1 ? "s" : ""
        } to specimen ${specimenLetter}`,
        color: "green",
      });
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err.message || "Failed to add blocks",
        color: "red",
      });
    }
  };

  const handleRemoveBlock = async (block: GrossingBlock) => {
    if (!block.isAdditional) {
      notifications.show({
        title: "Cannot Delete",
        message: "Original blocks created at accessioning cannot be deleted",
        color: "orange",
      });
      return;
    }

    // Prevent deleting the last block — specimen must have at least 1
    const remainingBlocks = blocks.filter((b) => b.id !== block.id);
    if (remainingBlocks.length === 0) {
      notifications.show({
        title: "Cannot Delete",
        message: "Specimen must have at least 1 block",
        color: "orange",
      });
      return;
    }

    try {
      await deleteBlockMutation.mutateAsync({
        caseId,
        blockId: block.id,
        specimenId,
      });

      notifications.show({
        title: "Block Removed",
        message: `Block ${specimenLetter}-${block.number} has been removed`,
        color: "blue",
      });
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err.message || "Failed to delete block",
        color: "red",
      });
    }
  };

  // Helper to sort labels alphabetically by specimen letter, then block number
  const sortLabels = (labels: CassetteLabel[]) => {
    return [...labels].sort((a, b) => {
      const letterCompare = a.specimenLetter.localeCompare(b.specimenLetter);
      if (letterCompare !== 0) return letterCompare;
      return parseInt(a.blockNumber, 10) - parseInt(b.blockNumber, 10);
    });
  };

  const handlePrintAll = async () => {
    const blockIds = blocks.map((b) => b.id);
    if (blockIds.length === 0) return;

    try {
      const result = await printLabelsMutation.mutateAsync({
        caseId,
        blockIds,
      });
      await printLabels(sortLabels(result.labels));
    } catch (err: any) {
      notifications.show({
        title: "Print Error",
        message: err.message || "Failed to print labels",
        color: "red",
      });
    }
  };

  const handlePrintSelected = async () => {
    if (selectedBlockIds.length === 0) {
      notifications.show({
        title: "No Selection",
        message: "Please select blocks to print",
        color: "orange",
      });
      return;
    }

    try {
      const result = await printLabelsMutation.mutateAsync({
        caseId,
        blockIds: selectedBlockIds,
      });
      await printLabels(sortLabels(result.labels));
      setSelectedBlockIds([]);
    } catch (err: any) {
      notifications.show({
        title: "Print Error",
        message: err.message || "Failed to print labels",
        color: "red",
      });
    }
  };

  const toggleBlockSelection = (blockId: string) => {
    setSelectedBlockIds((prev) =>
      prev.includes(blockId)
        ? prev.filter((id) => id !== blockId)
        : [...prev, blockId]
    );
  };

  // Count blocks by type
  const originalBlocks = blocks.filter((b) => !b.isAdditional);
  const additionalBlocks = blocks.filter((b) => b.isAdditional);

  if (isLoading) {
    return (
      <Paper withBorder p="md">
        <Group justify="center" py="md">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading blocks...
          </Text>
        </Group>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper withBorder p="md">
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          Failed to load blocks.{" "}
          <Text
            component="span"
            c="blue"
            style={{ cursor: "pointer" }}
            onClick={() => refetch()}
          >
            Retry
          </Text>
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper withBorder p="md">
      <Stack gap="sm">
        {/* Header */}
        <Group justify="space-between">
          <Group gap="xs">
            <IconPackage size={20} />
            <Text fw={600} size="sm">
              Blocks & Cassettes
            </Text>
          </Group>
          <Group gap="xs">
            {selectedBlockIds.length > 0 && (
              <Button
                size="xs"
                variant="light"
                color="blue"
                leftSection={<IconPrinter size={14} />}
                onClick={handlePrintSelected}
                loading={printLabelsMutation.isPending}
                disabled={disabled}
              >
                Print Selected ({selectedBlockIds.length})
              </Button>
            )}
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPrinter size={16} />}
              onClick={handlePrintAll}
              disabled={disabled || blocks.length === 0}
              loading={printLabelsMutation.isPending}
            >
              Print All
            </Button>
          </Group>
        </Group>

        <Divider />

        {/* Block Summary */}
        <Group gap="md">
          <div>
            <Text size="xs" c="dimmed" fw={500}>
              Total Blocks
            </Text>
            <Text size="lg" fw={700}>
              {blocks.length}
            </Text>
          </div>
          {originalBlocks.length > 0 && (
            <Badge color="gray" variant="light">
              {originalBlocks.length} original
            </Badge>
          )}
          {additionalBlocks.length > 0 && (
            <Badge color="blue" variant="light">
              +{additionalBlocks.length} new
            </Badge>
          )}
        </Group>

        {/* Block List (if any) */}
        {blocks.length > 0 && (
          <Stack gap="xs">
            {blocks.map((block) => (
              <Group
                key={block.id}
                justify="space-between"
                p="xs"
                bg={selectedBlockIds.includes(block.id) ? "blue.1" : "gray.0"}
                style={{
                  cursor: "pointer",
                  borderRadius: 4,
                  border: selectedBlockIds.includes(block.id)
                    ? "1px solid var(--mantine-color-blue-4)"
                    : "1px solid transparent",
                }}
                onClick={() => toggleBlockSelection(block.id)}
              >
                <Text size="sm" fw={500} ff="monospace">
                  {specimenLetter}-{block.number}
                </Text>
                <Group gap="xs">
                  {block.isAdditional ? (
                    <Badge size="xs" color="blue">
                      New
                    </Badge>
                  ) : (
                    <Badge size="xs" color="gray" variant="light">
                      Original
                    </Badge>
                  )}
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    leftSection={<IconPrinter size={14} />}
                    data-block-id={block.id}
                    onClick={handlePrintBlock}
                    disabled={disabled || !!printingBlockId}
                    loading={printingBlockId === block.id}
                  >
                    Print
                  </Button>
                  {block.isAdditional && (
                    <Tooltip label="Remove additional block">
                      <ActionIcon
                        size="sm"
                        color="red"
                        variant="subtle"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveBlock(block);
                        }}
                        disabled={disabled}
                        loading={deleteBlockMutation.isPending}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              </Group>
            ))}
          </Stack>
        )}

        {blocks.length === 0 && (
          <Text size="sm" c="dimmed" ta="center" py="md">
            No blocks created yet
          </Text>
        )}

        {/* Add Blocks Controls */}
        <Divider />
        <Group align="flex-end" gap="md">
          <NumberInput
            label="Add blocks"
            value={addCount}
            onChange={setAddCount}
            min={1}
            max={20}
            size="sm"
            disabled={disabled}
            style={{ width: 100 }}
          />
          <Button
            onClick={handleAddBlocks}
            leftSection={<IconPlus size={16} />}
            disabled={disabled || !addCount}
            loading={addBlocksMutation.isPending}
            size="sm"
          >
            Add
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}
